import * as React from "react";
import { FileTextIcon, PenLineIcon } from "../components/icons";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Tooltip } from "../components/ui/tooltip";

/**
 * "checkout" is not a switchable mode — it is what the pane shows in a host with no
 * other mode available, and it never appears in this control.
 */
export type AppMode = "references" | "draft" | "checkout";

export interface AppModeToggleProps {
  mode: AppMode;
  onChange: (mode: AppMode) => void;
  isAssembly: boolean;
  /** False in hosts with no content-control equivalent — the entry is hidden, not disabled. */
  assemblySupported?: boolean;
  /** False in hosts the assistant does not reach yet. */
  draftSupported?: boolean;
}

// Check out deliberately has NO entry here: it is not a workflow the user switches
// into, it is something they do to the open document via the header buttons.
const modes: { value: AppMode; label: string; icon: React.ReactNode; beta?: boolean }[] = [
  { value: "draft", label: "Draft", icon: <PenLineIcon className="size-4" />, beta: true },
  { value: "references", label: "Assembly", icon: <FileTextIcon className="size-4" /> },
];

/**
 * A segmented control for switching between the pane's modes.
 *
 * It used to be a hamburger that opened a full-width drawer holding a list of two
 * items. In a 350px pane that is a lot of ceremony for a binary choice, and it hid
 * which mode was active behind a click. The segments show both at once.
 */
const AppModeToggle = ({
  mode,
  onChange,
  isAssembly,
  assemblySupported = true,
  draftSupported = true,
}: AppModeToggleProps) => {
  // A mode the host cannot do at all is not shown greyed out — it is not offered.
  const visibleModes = modes.filter(
    (m) =>
      (m.value !== "references" || assemblySupported) && (m.value !== "draft" || draftSupported)
  );

  // Nothing to switch between: no control. A segmented control with one segment is a
  // label pretending to be a button.
  if (visibleModes.length < 2) return null;

  return (
    <ToggleGroup
      type="single"
      value={mode}
      aria-label="Mode"
      onValueChange={(next) => {
        // An exclusive group reports "" when the selected segment is pressed again.
        if (next) onChange(next as AppMode);
      }}
    >
      {visibleModes.map((m) => {
        const disabled = m.value === "references" && !isAssembly;
        const item = (
          <ToggleGroupItem key={m.value} value={m.value} disabled={disabled}>
            {m.icon}
            {m.label}
            {m.beta && (
              <span className="rounded-[2px] bg-grey-200 px-1 py-0.5 text-[9px] leading-none font-semibold tracking-[0.04em] text-grey-600 uppercase">
                Beta
              </span>
            )}
          </ToggleGroupItem>
        );
        // A disabled button fires no events, so the reason it is disabled needs a
        // wrapper to hang off. The group reads the value through context, so the
        // wrapper does not break selection.
        return disabled ? (
          <Tooltip key={m.value} title="Only available for assembly documents" side="bottom">
            <span className="inline-flex">{item}</span>
          </Tooltip>
        ) : (
          item
        );
      })}
    </ToggleGroup>
  );
};

export default AppModeToggle;
